/**
 * WikiPage — in-app help / wiki (NC-597, static MVP).
 *
 * Reads `public/wiki/index.json` for the article manifest and `public/wiki/<id>.md`
 * for each article body. Renders via the in-tree MarkdownRenderer (no react-markdown
 * dep). Articles are grouped by `category` in the sidebar.
 *
 * Out-of-scope for the static MVP: admin edit mode, search, dynamic Cosmos-backed
 * articles. The dynamic variant is the BE-1..INFRA-1 follow-up tracked on #597.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import MarkdownRenderer from '../components/MarkdownRenderer';
import ErrorMessage from '../components/ErrorMessage';

interface WikiArticleMeta {
  id: string;
  title: string;
  category: string;
  summary?: string;
}

interface WikiManifest {
  articles: WikiArticleMeta[];
}

export default function WikiPage() {
  const [manifest, setManifest] = useState<WikiManifest | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [articleBody, setArticleBody] = useState<string | null>(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const [articleError, setArticleError] = useState<string | null>(null);

  // Load the manifest once on mount.
  useEffect(() => {
    let cancelled = false;
    fetch('/wiki/index.json')
      .then((res) => {
        if (!res.ok) throw new Error(`Manifest HTTP ${res.status}`);
        return res.json();
      })
      .then((data: WikiManifest) => {
        if (cancelled) return;
        setManifest(data);
        if (data.articles.length > 0) setSelectedId(data.articles[0].id);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setManifestError(err instanceof Error ? err.message : 'Manifest konnte nicht geladen werden');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the selected article's body whenever selection changes.
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setArticleLoading(true);
    setArticleError(null);
    setArticleBody(null);
    fetch(`/wiki/${selectedId}.md`)
      .then((res) => {
        if (!res.ok) throw new Error(`Artikel HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setArticleBody(text);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setArticleError(err instanceof Error ? err.message : 'Artikel konnte nicht geladen werden');
      })
      .finally(() => {
        if (!cancelled) setArticleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  if (manifestError) {
    return (
      <Box p={3}>
        <ErrorMessage
          title="Wiki konnte nicht geladen werden"
          message={manifestError}
          onRetry={() => window.location.reload()}
        />
      </Box>
    );
  }

  if (!manifest) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (manifest.articles.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Wiki
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Noch keine Artikel veröffentlicht.
        </Typography>
      </Box>
    );
  }

  // Group articles by category for the sidebar.
  const grouped = manifest.articles.reduce<Record<string, WikiArticleMeta[]>>((acc, a) => {
    (acc[a.category] = acc[a.category] || []).push(a);
    return acc;
  }, {});

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Wiki
      </Typography>
      <Box display="flex" gap={3} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        <Paper sx={{ minWidth: 240, maxWidth: 320, flex: '0 0 auto' }}>
          {Object.entries(grouped).map(([category, items], idx) => (
            <React.Fragment key={category}>
              {idx > 0 && <Divider />}
              <Box px={2} pt={2} pb={1}>
                <Typography variant="overline" color="text.secondary">
                  {category}
                </Typography>
              </Box>
              <List dense sx={{ pt: 0 }}>
                {items.map((article) => (
                  <ListItem key={article.id} disablePadding>
                    <ListItemButton
                      selected={article.id === selectedId}
                      onClick={() => setSelectedId(article.id)}
                    >
                      <ListItemText
                        primary={article.title}
                        secondary={article.summary}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </React.Fragment>
          ))}
        </Paper>

        <Card sx={{ flex: 1, minHeight: 400 }}>
          <CardContent>
            {articleLoading && (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            )}
            {!articleLoading && articleError && (
              <ErrorMessage
                title="Artikel konnte nicht geladen werden"
                message={articleError}
                onRetry={() => {
                  // Force-refetch by toggling selection.
                  const id = selectedId;
                  setSelectedId(null);
                  setTimeout(() => setSelectedId(id), 0);
                }}
              />
            )}
            {!articleLoading && !articleError && articleBody !== null && (
              <MarkdownRenderer source={articleBody} />
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
